import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Users, Gauge, Lock, Bell, Settings } from "lucide-react";
import { useLang } from "../context/I18nContext";
import SectionVideo from "../components/SectionVideo";
import { SECTION_VIDEOS } from "../config/sectionVideos";

/**
 * Wohnanlagen — für Bauträger, Projektentwickler, Hausverwaltungen
 */
export default function Wohnanlagen() {
  const { t } = useLang();

  const topics = [
    { icon: Building2, title: "Zentrale Heizsysteme", d: "Ein zentrales System mit Zuordnung je Wohnung." },
    { icon: Users, title: "Wohnungsregelung", d: "Individuelle Regelung pro Wohneinheit." },
    { icon: Gauge, title: "Verbrauchserfassung", d: "Zuordnung von Wärme, Warmwasser und Strom." },
    { icon: Settings, title: "Allgemeinflächen", d: "Beleuchtung, Lüftung, Steuerung gemeinsamer Bereiche." },
    { icon: Lock, title: "Zutritt & Türkommunikation", d: "Zutrittsberechtigungen, Video, Gegensprechen." },
    { icon: Bell, title: "Energiemanagement & Monitoring", d: "Zentral — mit Zugriff für Hausverwaltung." },
  ];

  return (
    <div className="page-shell" data-testid="wohnanlagen-page">
      <section className="site-container page-hero">
        <div className="eyebrow mb-6 text-[#5FA637]">04 · {t("nav.residential")}</div>
        <h1 className="page-title">
          Gebäudetechnik<br/>
          <span className="italic-accent">für Wohnanlagen.</span>
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          Für Bauträger, Projektentwickler, Hausverwaltungen und Eigentümergemeinschaften. Zentrale Technik mit
          wohnungsweiser Regelung, Verbrauchszuordnung und zentraler Betreuung.
        </p>
      </section>

      {/* Section video — Wohnanlagen */}
      <SectionVideo {...SECTION_VIDEOS.wohnanlagen}/>

      <section className="site-container section-block border-t border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 reveal-stagger">
          {topics.map((topic, i) => (
            <div key={i} className="technical-card p-8">
              <topic.icon size={26} strokeWidth={1.5} className="text-[#5FA637] mb-5"/>
              <div className="font-display text-xl mb-3">{topic.title}</div>
              <p className="text-sm text-white/60 leading-relaxed">{topic.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Neubau vs. Sanierung */}
      <section className="site-container section-block border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 reveal">
          <div className="technical-card p-8 md:p-10">
            <div className="eyebrow mb-4 text-[#5FA637]">Neubau</div>
            <h3 className="font-display text-2xl md:text-3xl mb-5 leading-tight">Frühzeitige Planung zahlt sich aus.</h3>
            <ul className="space-y-2 text-sm text-white/80">
              {["Leitungsführung", "Technikräume", "Automationsvorbereitung", "Elektro", "Heizung", "PV", "Sensorik", "Zutritt", "Beschattung"].map((x, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#5FA637]"/>
                  {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="technical-card p-8 md:p-10">
            <div className="eyebrow mb-4 text-[#5FA637]">Sanierung</div>
            <h3 className="font-display text-2xl md:text-3xl mb-5 leading-tight">Nicht alles muss ersetzt werden.</h3>
            <p className="text-white/75 leading-relaxed mb-4">Wir prüfen gemeinsam:</p>
            <ul className="space-y-2 text-sm text-white/80">
              {["Was kann bleiben?", "Was muss ersetzt werden?", "Was kann erweitert werden?", "Wo ist Funk sinnvoll?", "Wo sind neue Leitungen sinnvoll?"].map((x, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#5FA637]"/>
                  {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="site-container section-block">
        <div className="cta-panel">
          <div className="eyebrow mb-5 text-[#5FA637]">Bauträger & Hausverwaltung</div>
          <h2 className="section-title max-w-3xl">
            Projekt mit zentraler <span className="italic-accent">Technik-Koordination.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/kontakt" className="btn-primary">
              {t("cta.inquiry")} <ArrowRight size={14}/>
            </Link>
            <Link to="/planung-bim" className="btn-ghost">
              {t("nav.planungBim")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
